import { CapacitorHttp } from "@capacitor/core";
import { ApiOptionsData, ApiSession } from "../models/api/ApiOptions";
import { getPreBootstrapConfig } from "./ApiHelper";
import { getPortalBearerToken, getAuthConfig } from "./OAuth";
import { get, remove, set, _iamKey, getAuthTypePreference } from "./Preferences";

const _apiOptionsDataKey = "ApiOptionsData";
export const _apiSessionKey = "ApiSession";
const _qrPlantDB = "QRPlantDB";

export interface QRPlantDB {
  DBName: string;
  PlantCode: string;
}

export const getApiSession = async () => {
  let apiSession = (await get(_apiSessionKey)) as ApiSession;
  //return valid value, if undefined or null
  if (apiSession === undefined) {
    apiSession = {
      BackendApiUrl: "",
      DatabaseName: "",
      LicenseExpiration: 0,
      LicenseIssuedAt: 0,
      LicenseSessionId: "",
      PlantCode: "",
      B2CUserFlowScope: "",
    };
  }
  //update api session with QRPlantDB if available
  return await updateApiSessionWithQRPlantDB(apiSession);
};

export const setApiSession = async (apiSession: ApiSession) => {
  await clearApiSession();
  return await set(_apiSessionKey, apiSession);
};

export const clearApiSession = async () => {
  await remove(_apiSessionKey);
};

export const getApplication = async (dbName: string) => {
  let apiOptionsData = await getApiOptionsData();
  // ApiOptionsData may not be populated yet on a cold-cache portal/QR launch
  // (QRPlantDB is set before mobileBootstrap runs). Guard so we defer rather
  // than throw; QRPlantDB stays until it can be resolved.
  const application = apiOptionsData?.Applications?.find((app) => {
    return app.DatabaseName === dbName;
  });
  return application !== undefined ? application : null;
};

export const getPlant = async (pCode: string) => {
  let apiOptionsData = await getApiOptionsData();
  // See getApplication: guard against ApiOptionsData not being bootstrapped yet.
  const plant = apiOptionsData?.Plants?.find((plant) => {
    return plant.PlantCode === pCode;
  });
  return plant !== undefined ? plant : null;
};

export const getCurrentPlant = async () => {
  const apiSession = await getApiSession();
  return !!apiSession ? apiSession.PlantCode : "";
};

export const getApiOptionsData = async (): Promise<ApiOptionsData> => {
  return (await get(_apiOptionsDataKey)) as ApiOptionsData;
};

export const clearApiOptionsData = async () => {
  await remove(_apiOptionsDataKey);
};

export const mobileBootstrap = async () => {
  const portalBearerToken = await getPortalBearerToken();
  const preBootstrapConfig = await getPreBootstrapConfig();
  const response = await CapacitorHttp.get({
    method: "GET",
    url: `${preBootstrapConfig.MOBILE_BOOTSTRAP_API_BASE_ADDRESS}Mobile/MobileBootstrap`,
    headers: {
      authorization: `Bearer ${portalBearerToken}`,
    },
  });
  if (response.status === 200) {
    const apiOptionsData = await createApiOptionsData(response.data);
    const iamType = await getAuthTypePreference(_iamKey);
    if (iamType?.value === 'ApteanIAM') {
      apiOptionsData.Applications.forEach(application => {
        application.B2CUserFlowScope = 'openid profile offline_access';
      });
    }
    await clearApiOptionsData();
    await set(_apiOptionsDataKey, apiOptionsData);
  } else {
    console.log("Mobilebootstrap error: ", response.data);
  }
};

export const getQRPlantDB = async () => {
  return (await get(_qrPlantDB)) as QRPlantDB;
};

export const setQRPlantDB = async (qrPlantDB: QRPlantDB) => {
  await clearQRPlantDB();
  if (!!qrPlantDB) {
    await set(_qrPlantDB, qrPlantDB);
  }
};

export const clearQRPlantDB = async () => {
  await remove(_qrPlantDB);
};

export const clearSiteData = async () => {
  // Wait for every cache to be deleted so callers can safely reload afterwards.
  if (typeof caches !== "undefined") {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
  await remove(_iamKey);
};

const createApiOptionsData = async (resposneData: any) => {
  let apiOptionsData: ApiOptionsData = {
    Applications: [],
    Plants: [],
  };
  if (resposneData.applications) {
    let authConfig = await getAuthConfig(),
      scope = authConfig ? authConfig.SCOPE : '';
    const preBootstrapConfig = await getPreBootstrapConfig();
    resposneData.applications.forEach((app: any) => {
      const backendApiUrl: string = !!app.backendApiUrl
        ? app.backendApiUrl
        : preBootstrapConfig.API_BASE_ADDRESS;
      // Push application if invocationUrl is absent, or if it matches the publicUrl origin
      if (!app.invocationUrl || app.invocationUrl === preBootstrapConfig.INVOCATION_URL) {
        apiOptionsData.Applications.push({
          BackendApiUrl: backendApiUrl.endsWith("/")
            ? backendApiUrl
            : backendApiUrl + "/",
          DatabaseName: app.databaseName,
          Description: app.description,
          ProductionDatabaseFlag: app.productionDatabaseFlag,
          B2CUserFlowScope: !!app.b2CUserFlowScope
            ? app.b2CUserFlowScope.join(" ")
            : scope,
        });
      }
    });
  }
  if (resposneData.plants) {
    resposneData.plants.forEach((plant: any) => {
      apiOptionsData.Plants.push({
        Description: plant.description,
        PlantCode: plant.plantCode,
      });
    });
  }
  return apiOptionsData;
};

const updateApiSessionWithQRPlantDB = async (apiSession: ApiSession) => {
  const qrPlantDB = await getQRPlantDB();
  if (!!qrPlantDB) {
    const plant = await getPlant(qrPlantDB.PlantCode);
    const application = await getApplication(qrPlantDB.DBName);
    if (!!plant && !!application) {
      apiSession.PlantCode = plant.PlantCode;
      apiSession.DatabaseName = application.DatabaseName;
      apiSession.BackendApiUrl = application.BackendApiUrl;
      apiSession.B2CUserFlowScope = application.B2CUserFlowScope;

      //update stored api session
      await setApiSession(apiSession);
      //clear qr plant/db
      clearQRPlantDB();
    }
  }
  return apiSession;
};
